//changed due to package upgrade
import React, { useState, useEffect, useRef } from 'react';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import PropTypes from 'prop-types';
import {
  alpha,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Divider,
  FormControl,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import AppGridContainer from '@projectK/components/AppGridContainer';
import EditIcon from '@mui/icons-material/Edit';
import Grid from '@mui/material/Grid';
import IntlMessages from '@projectK/helpers/IntlMessages';
import { useDropzone } from 'react-dropzone';
import { useInfoViewActionsContext } from '@projectK/context/InfoViewContextProvider';
import jwtAxios from '@projectK/services/auth/JWT';
import { Fonts } from '@projectK/constants/AppEnums';
import AppTextField from '@projectK/components/AppTextField';
import AppInfoView from '@projectK/components/AppInfoView';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import styled from '@emotion/styled';
import { HandleMobileFormat } from '@projectK/ReUseFunctions';
import {
  isStudent,
  isCoach,
  hasPermission,
  useRolePermissions,
} from '@projectK/context/RolesProvider';

const AvatarViewWrapper = styled('div')(({ theme }) => {
  return {
    position: 'relative',
    cursor: 'pointer',
    '& .profile-edit-icon': {
      position: 'absolute',
      bottom: 0,
      right: 0,
      zIndex: 1,
      border: `solid 2px ${theme.palette.background.paper}`,
      backgroundColor: alpha(theme.palette.primary.main, 0.7),
      color: theme.palette.primary.contrastText,
      borderRadius: '50%',
      width: 26,
      height: 26,
      display: 'none',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.4s ease',
      cursor: 'pointer',
      '& .MuiSvgIcon-root': {
        fontSize: 16,
      },
    },
    '&.dropzone': {
      outline: 0,
      '&:hover .profile-edit-icon, &:focus .profile-edit-icon': {
        display: 'flex',
      },
    },
  };
});

const studentValidationSchema = yup.object({
  first_name: yup
    .string()
    .nullable()
    .required(<IntlMessages id='validation.firstNameRequired' />),
  last_name: yup
    .string()
    .nullable()
    .required(<IntlMessages id='validation.lastNameRequired' />),
  user_mobile: yup
    .string()
    .matches(/^\d{3}-\d{3}-\d{4}$/, () => (
      <IntlMessages id='validation.invalidPhoneNumber' />
    ))
    .nullable()
    .optional(),
  email: yup
    .string()
    .matches(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/, () => (
      <IntlMessages id='validation.invalidEmailFormat' />
    ))
    .typeError(<IntlMessages id='validation.invalidEmail' />)
    .required(<IntlMessages id='validation.emailRequired' />),
  biodata: yup
    .string()
    .nullable()
    .optional()
    .test(
      'max-50kb',
      () => <IntlMessages id='validation.bioDataTooLarge' />,
      (value) => {
        if (!value) return true;
        return new Blob([value]).size <= 51200;
      },
    ),
  parent1_mobile: yup
    .string()
    .matches(/^\d{3}-\d{3}-\d{4}$/, () => (
      <IntlMessages id='validation.invalidPhoneNumber' />
    ))
    .nullable()
    .optional(),
  parent2_mobile: yup
    .string()
    .matches(/^\d{3}-\d{3}-\d{4}$/, () => (
      <IntlMessages id='validation.invalidPhoneNumber' />
    ))
    .nullable()
    .optional(),
  parent1_name: yup.string().nullable().optional(),
  parent2_name: yup.string().nullable().optional(),
  address: yup
    .string()
    .max(255, () => <IntlMessages id='validation.addressTooLong' />)
    .nullable()
    .optional(),
  zip_code: yup
    .string()
    .nullable()
    .optional()
    .matches(/^(\d{1,8}|[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d)$/, () => (
      <IntlMessages id='validation.zipCodeFormat' />
    )),
  jersey_size: yup
    .string()
    .matches(/^[A-Za-z0-9]+$/, () => (
      <IntlMessages id='validation.invalidJerseySize' />
    ))
    .nullable()
    .optional(),
  act_score: yup
    .number()
    .typeError(<IntlMessages id='validation.invalidActScore' />)
    .nullable()
    .optional()
    .min(1, () => <IntlMessages id='validation.actScoreTooLow' />)
    .max(36, () => <IntlMessages id='validation.actScoreTooHigh' />),
  gpa: yup
    .number()
    .nullable()
    .optional()
    .min(0, () => <IntlMessages id='validation.gpaTooLow' />)
    .max(5.0, () => <IntlMessages id='validation.gpaTooHigh' />)
    .positive(<IntlMessages id='validation.gpaNotPositive' />)
    .typeError(<IntlMessages id='validation.invalidGpa' />),
  emergency_contact_name: yup.string().nullable().optional(),
  emergency_contact_number: yup
    .string()
    .nullable()
    .optional()
    .matches(/^\d{3}-\d{3}-\d{4}$/, () => (
      <IntlMessages id='validation.invalidPhoneNumber' />
    )),
  relation: yup.string().nullable().optional(),
});
const parentValidationSchema = yup.object({
  first_name: yup
    .string()
    .nullable()
    .required(<IntlMessages id='validation.firstNameRequired' />),
  last_name: yup
    .string()
    .nullable()
    .required(<IntlMessages id='validation.lastNameRequired' />),
  user_mobile: yup
    .string()
    .matches(/^\d{3}-\d{3}-\d{4}$/, () => (
      <IntlMessages id='validation.invalidPhoneNumber' />
    ))
    .nullable()
    .optional(),
  email: yup
    .string()
    .matches(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/, () => (
      <IntlMessages id='validation.invalidEmailFormat' />
    ))
    .typeError(<IntlMessages id='validation.invalidEmail' />)
    .required(<IntlMessages id='validation.emailRequired' />),
  biodata: yup
    .string()
    .nullable()
    .optional()
    .test(
      'max-50kb',
      () => <IntlMessages id='validation.bioDataTooLarge' />,
      (value) => {
        if (!value) return true;
        return new Blob([value]).size <= 51200;
      },
    ),
  parent1_mobile: yup
    .string()
    .matches(/^\d{3}-\d{3}-\d{4}$/, () => (
      <IntlMessages id='validation.invalidPhoneNumber' />
    ))
    .nullable()
    .optional(),
  parent2_mobile: yup
    .string()
    .matches(/^\d{3}-\d{3}-\d{4}$/, () => (
      <IntlMessages id='validation.invalidPhoneNumber' />
    ))
    .nullable()
    .optional(),
  parent1_name: yup
    .string()

    .nullable()
    .optional(),
  parent2_name: yup.string().nullable().optional(),
  address: yup
    .string()
    .max(255, () => <IntlMessages id='validation.addressTooLong' />)
    .nullable()
    .optional(),
  zip_code: yup
    .string()
    .nullable()
    .optional()
    .matches(/^(\d{1,8}|[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d)$/, () => (
      <IntlMessages id='validation.zipCodeFormat' />
    )),
  jersey_size: yup
    .string()
    .matches(/^[A-Za-z0-9]+$/, () => (
      <IntlMessages id='validation.invalidJerseySize' />
    ))
    .nullable()
    .optional(),
  act_score: yup
    .number()
    .typeError(<IntlMessages id='validation.invalidActScore' />)
    .nullable()
    .optional()
    .min(1, () => <IntlMessages id='validation.actScoreTooLow' />)
    .max(36, () => <IntlMessages id='validation.actScoreTooHigh' />),
  gpa: yup
    .number()
    .nullable()
    .optional()
    .min(0, () => <IntlMessages id='validation.gpaTooLow' />)
    .max(5.0, () => <IntlMessages id='validation.gpaTooHigh' />)
    .positive(<IntlMessages id='validation.gpaNotPositive' />)
    .typeError(<IntlMessages id='validation.invalidGpa' />),
  emergency_contact_name: yup.string().nullable().optional(),
  emergency_contact_number: yup
    .string()
    .nullable()
    .optional()
    .matches(/^\d{3}-\d{3}-\d{4}$/, () => (
      <IntlMessages id='validation.invalidPhoneNumber' />
    )),
  relation: yup.string().nullable().optional(),
});

const getCorrectValues = ({ values, rest }) => {
  const normalize = (val) => (val === '' ? null : val);
  const fieldsToNormalize = [
    'user_mobile',
    'parent1_name',
    'parent1_mobile',
    'parent2_name',
    'parent2_mobile',
    'address',
    'jersey_size',
    'act_score',
    'gpa',
    'zip_code',
    'emergency_contact_number',
    'emergency_contact_name',
    'relation',
    'campus_id',
  ];

  const normalizedValues = fieldsToNormalize.reduce((acc, field) => {
    acc[field] = normalize(values?.[field]);
    return acc;
  }, {});

  return {
    ...normalizedValues,
    ...rest,
  };
};
const getInitialValues = ({ settings = {} }) => {
  const {
    first_name = '',
    last_name = '',
    email = '',
    user_mobile,
    roles = [],
    user_profile_pic = '',
    biodata = '',
    parent1_name = '',
    parent1_mobile,
    parent2_name = '',
    parent2_mobile,
    address = '',
    zip_code,
    jersey_size = '',
    act_score = '',
    gpa,
    emergency_contact_number,
    emergency_contact_name = '',
    relation = '',
    campus_id = null,
    city = '',
    state = '',
    country = '',
  } = settings;

  return {
    first_name,
    last_name,
    email,
    user_mobile: HandleMobileFormat(user_mobile, true) || '',
    roleName: roles[0]?.name || '',
    user_profile_pic,
    biodata,
    parent1_name,
    parent1_mobile: HandleMobileFormat(parent1_mobile, true) || '',
    parent2_name,
    parent2_mobile: HandleMobileFormat(parent2_mobile, true) || '',
    address,
    zip_code: zip_code?.toString() || '',
    jersey_size,
    act_score,
    gpa: gpa?.toString() || '',
    emergency_contact_number:
      HandleMobileFormat(emergency_contact_number, true) || '',
    emergency_contact_name,
    relation,
    campus_id,
    city,
    state,
    country,
  };
};

const CommonAccountInfo = ({ settings, fetchData }) => {
  const { rolePermissions, roleData } = useRolePermissions();
  const companyTheme = CompanyTheme();
  const infoViewActionsContext = useInfoViewActionsContext();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [states, setStates] = useState([]);
  const [country, setCountry] = React.useState();
  const dropzone = useDropzone({
    accept: {
      'image/png': ['.png', '.jpeg', '.jpg'],
    },
    onDrop: (acceptedFiles) => {
      // Append new files to the existing uploadedFiles array
      setUploadedFiles((prevFiles) => [
        ...prevFiles,
        ...acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          }),
        ),
      ]);
    },
  });
  useEffect(() => {
    setUploadedFiles(dropzone.acceptedFiles);
  }, [dropzone.acceptedFiles]);

  const { user } = useAuthUser();

  const editorRef = useRef(null);

  const [isFullBioVisible, setIsFullBioVisible] = useState(false);

  const handleReadMore = () => {
    setIsFullBioVisible(true);
  };

  const handleReadLess = () => {
    setIsFullBioVisible(false);
  };
  const validationSchema = (userData) => {
    if (isStudent(userData)) {
      return studentValidationSchema;
    } else {
      return parentValidationSchema;
    }
  };

  let bioContent = '';
  if (settings?.biodata) {
    if (isFullBioVisible) {
      bioContent = settings.biodata;
    } else {
      bioContent = settings.biodata.slice(0, 200);
    }
  }

  const handleMobileFormat = (event, name) => {
    let inputValue = event.target.value.replace(/\D/g, ''); // Remove non-numeric characters
    let formattedValue = '';

    // Ensure the first group of digits is not longer than 3 digits
    if (inputValue.length > 3) {
      inputValue = inputValue.slice(0, 3) + '-' + inputValue.slice(3);
    }

    // Ensure the second group of digits is not longer than 6 digits
    if (inputValue.length > 7) {
      inputValue = inputValue.slice(0, 7) + '-' + inputValue.slice(7);
    }

    // Ensure the total length does not exceed 12 characters
    formattedValue = inputValue;

    // Update form field value
    event.target.value = formattedValue; // Set input value to formatted value

    if (name === 'user_mobile') {
      return { name: 'user_mobile', value: formattedValue || null };
    } else if (name === 'parent1_mobile') {
      return { name: 'parent1_mobile', value: formattedValue || null };
    } else if (name === 'parent2_mobile') {
      return { name: 'parent2_mobile', value: formattedValue || null };
    } else if (name === 'emergency_contact_number') {
      return {
        name: 'emergency_contact_number',
        value: formattedValue || null,
      };
    }
  };
  const handleConditionFunction = (condition, ifData, elseData) => {
    if (condition) {
      return ifData;
    } else {
      return elseData;
    }
  };
  useEffect(() => {
    fetchStates();
  }, [country]);

  const fetchStates = async () => {
    try {
      const response = await jwtAxios.get(`/states`, {
        params: {
          country,
          perPage: 'all',
        },
      });
      const { data } = response;
      setStates(data.list);
    } catch (error) {
      setStates([]);
    }
  };
  return (
    <>
      <Box
        className={'profile-info-client-logo'}
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: { xs: 5, lg: 6 },
          }}
        >
          <AvatarViewWrapper
            {...dropzone.getRootProps({ className: 'dropzone' })}
          >
            <input {...dropzone.getInputProps()} />
            <label htmlFor='icon-button-file'>
              <Avatar
                sx={{
                  width: { xs: 70, lg: 84 },
                  height: { xs: 70, lg: 84 },
                  cursor: 'pointer',
                }}
                src={
                  uploadedFiles?.[0]?.preview ||
                  (settings?.user_profile_pic
                        ? `${process.env.NX_CLOUD_FRONT_URL}/${settings?.user_profile_pic}`
                        : '/assets/images/profileAvatar.png')
                }
              />
              <Box className='profile-edit-icon'>
                <EditIcon />
              </Box>
            </label>
          </AvatarViewWrapper>
          <Box
            sx={{
              ml: 4,
            }}
          >
            <Typography
              sx={{
                fontWeight: Fonts.BOLD,
                fontSize: 18,
              }}
            >
              {settings?.first_name} {settings?.last_name}
            </Typography>
            <Typography
              sx={{
                color: '#000000',
              }}
            >
              {settings?.email}
            </Typography>
            <Typography
              sx={{
                color: '#5c5050',
              }}
            >
              {roleData?.role_name}
            </Typography>
            <Typography style={{ color: '#000000', marginTop: 5 }}>
              <div dangerouslySetInnerHTML={{ __html: bioContent }} />
              {settings?.biodata?.length > 200 && (
                <div
                  style={{
                    textAlign: 'right',
                  }}
                >
                  {!isFullBioVisible && <span>... </span>}
                  <Button
                    onClick={isFullBioVisible ? handleReadLess : handleReadMore}
                    sx={{
                      color: companyTheme
                        ? companyTheme.primary.main
                        : '#2196f3',
                    }}
                  >
                    {isFullBioVisible ? 'Read Less' : 'Read More'}
                  </Button>
                </div>
              )}
            </Typography>
          </Box>
        </Box>
        <Box className={'client-logo'} sx={{ display: 'flex', gap: 2 }}>
          <Tooltip title='Sign in to CoachNow'>
            <a
              href='https://app.coachnow.io/signin'
              target='_blank'
              rel='noopener noreferrer'
              aria-label='CoachNow login'
              style={{ display: 'inline-block' }}
            >
              <img //NOSONAR
                src='/assets/images/CoachNow.png'
                alt='CoachNow logo'
                style={{
                  width: '150px',
                  transition: 'transform 0.2s ease',
                }}
                onMouseOver={(e) =>//NOSONAR
                  (e.currentTarget.style.transform = 'scale(1.05)')
                }
                onMouseOut={(e) =>//NOSONAR
                  (e.currentTarget.style.transform = 'scale(1)')
                }
              />
            </a>
          </Tooltip>

          <Tooltip title='Sign in to Teambuildr'>
            <a
              href='https://app-v3.teambuildr.com/login'
              target='_blank'
              rel='noopener noreferrer'
              aria-label='Teambuildr login'
              style={{ display: 'inline-block' }}
            >
              <img //NOSONAR
                src='/assets/images/Teambuilder.png'
                alt='Teambuildr logo'
                style={{
                  width: '150px',
                  transition: 'transform 0.2s ease',
                }}
                onMouseOver={(e) =>//NOSONAR
                  (e.currentTarget.style.transform = 'scale(1.05)')
                }
                onMouseOut={(e) =>//NOSONAR
                  (e.currentTarget.style.transform = 'scale(1)')
                }
              />
            </a>
          </Tooltip>
        </Box>
      </Box>
      <Divider
        sx={{
          marginTop: 4,
          marginBottom: 5,
        }}
      />
      <Box>
        <Formik
          enableReinitialize={true}
          validateOnBlur={true}
          initialValues={getInitialValues({ settings })}
          validationSchema={validationSchema(user)}
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            setSubmitting(true);

            let imageUrl = values.user_profile_pic; // Use the existing image URL by default
            if (uploadedFiles && uploadedFiles.length > 0) {
              const file = uploadedFiles[0];

              const formData = new FormData();
              formData.append('file', file);

              try {
                const response = await jwtAxios.post(`/fileuploads`, formData, {
                  headers: {
                    'Content-Type': 'multipart/form-data',
                  },
                });
                const bgFileName = await response.data;
                imageUrl = bgFileName.file;
              } catch (error) {
                console.error(
                  'Error uploading file: ',
                  error.message ||
                    error.data.message ||
                    error.data.error ||
                    error,
                );
              }
            }

            const {
              id,
              displayName,
              roleName,
              role,
              jersey_size,
              user_mobile,
              parent1_name,
              parent1_mobile,
              parent2_name,
              parent2_mobile,
              address,
              act_score,
              gpa,
              zip_code,
              emergency_contact_name,
              emergency_contact_number,
              relation,
              campus_id,
              ...rest
            } = values;
            const reqData = getCorrectValues({ rest, values });
            await jwtAxios
              .put(`/users/${user.id}`, {
                ...reqData,
                ...(imageUrl && { user_profile_pic: imageUrl }),
              })
              .then(() => {
                fetchData();
                setSubmitting(false);
                infoViewActionsContext.showMessage(
                  'Personal Info Updated Successfully',
                );
                setTimeout(() => {
                  window.location.reload();
                }, 2000);
              })
              .catch((error) => {
                infoViewActionsContext.fetchError(error.response.data.error);
                setSubmitting(false);
              });
          }}
        >
          {({
            values,
            setFieldValue,
            handleChange,
            handleBlur,
            resetForm,
            touched,
            errors,
          }) => {
            return (
              <div className='mobile-form'>
                <Form noValidate autoComplete='off'>
                  <Grid size={{ xs: 12, md: 12 }} sx={{ marginTop: 2 }}>
                    <AppGridContainer>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <AppTextField
                          name='first_name'
                          fullWidth
                          label={<IntlMessages id='common.firstName' />}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.first_name}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <AppTextField
                          fullWidth
                          name='last_name'
                          value={values.last_name}
                          label={<IntlMessages id='common.lastName' />}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <AppTextField
                          name='email'
                          value={values.email}
                          fullWidth
                          label={<IntlMessages id='common.email' />}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          name='user_mobile'
                          value={values.user_mobile}
                          slotProps={{
                            input: { maxLength: 12 },
                          }}
                          error={!!errors.user_mobile}
                          helperText={errors.user_mobile}
                          onChange={(event) => {
                            setFieldValue(
                              handleMobileFormat(event, 'user_mobile').name,
                              handleMobileFormat(event, 'user_mobile').value,
                            );
                          }}
                          fullWidth
                          label={<IntlMessages id='common.phoneNumber' />}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <Stack spacing={1} style={{ marginBottom: '50px' }}>
                          <Typography
                            variant='h5'
                            style={{ marginBottom: '5px', marginLeft: '8px' }}
                          >
                            Bio
                          </Typography>
                          <ReactQuill
                            style={{ height: '200px' }}
                            ref={editorRef}
                            value={values.biodata}
                            onChange={(content, delta, source, editor) => {
                              setFieldValue('biodata', content);
                            }}
                            onBlur={(range, source, quill) => {
                              handleBlur({ target: { name: 'biodata' } });
                            }}
                            modules={{
                              toolbar: [
                                [
                                  { header: '1' },
                                  { header: '2' },
                                  { font: [] },
                                ],
                                [{ list: 'ordered' }, { list: 'bullet' }],
                                [
                                  'bold',
                                  'italic',
                                  'underline',
                                  'strike',
                                  'blockquote',
                                ],
                                [{ align: [] }],
                                ['link', 'image'],
                                ['clean'],
                              ],
                            }}
                            formats={[
                              'header',
                              'font',
                              'list',
                              'bold',
                              'italic',
                              'underline',
                              'strike',
                              'blockquote',
                              'align',
                              'link',
                              'image',
                            ]}
                          />
                        </Stack>
                        {errors.biodata && (
                          <div
                            className='error'
                            style={{
                              color: '#d32f2f',
                              fontSize: '13px',
                              marginLeft: '13px',
                              marginTop: '6px',
                            }}
                          >
                            {errors.biodata}
                          </div>
                        )}
                      </Grid>
                    </AppGridContainer>
                    {isCoach(user) && (
                      <>
                        <Divider
                          sx={{
                            marginTop: 14,
                            marginBottom: 5,
                          }}
                        />
                        <Typography
                          component='h3'
                          sx={{
                            fontSize: 16,
                            fontWeight: Fonts.BOLD,
                            mb: { xs: 3, lg: 4 },
                            color: handleConditionFunction(
                              companyTheme,
                              companyTheme?.primary?.main,
                              'text.primary',
                            ),
                          }}
                        >
                          <IntlMessages id='common.addressInfo' />
                        </Typography>
                        <AppGridContainer sx={{ my: 2 }}>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <AppTextField
                              name='address'
                              value={values.address}
                              onChange={(event) => {
                                setFieldValue(
                                  'address',
                                  event.target.value === ''
                                    ? null
                                    : event.target.value,
                                );
                              }}
                              variant='outlined'
                              sx={{
                                width: '100%',
                              }}
                              label='Address'
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl
                              sx={{ width: '100%', borderRadius: 50 }}
                            >
                              <TextField
                                sx={{
                                  width: '100%',
                                }}
                                select
                                name='country'
                                value={values.country}
                                label='Country'
                                error={
                                  touched.country && Boolean(errors.country)
                                }
                                helperText={touched.country && errors.country}
                                onChange={(event) => {
                                  setCountry(event.target.value);
                                  setFieldValue('country', event.target.value); // Update the form field value
                                }}
                              >
                                <MenuItem value='us'>US</MenuItem>
                                <MenuItem value='ca'>CA</MenuItem>
                              </TextField>
                            </FormControl>
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Autocomplete
                              id='state-autocomplete'
                              options={states}
                              getOptionLabel={(option) =>
                                `${option.name} - ${option.abbreviation}`
                              }
                              value={
                                states?.find(
                                  (state) =>
                                    state.abbreviation === values.state,
                                ) || null
                              }
                              onChange={(event, newValue) => {
                                setFieldValue(
                                  'state',
                                  newValue ? newValue.abbreviation : '',
                                );
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label='State'
                                  variant='outlined'
                                  error={touched.state && Boolean(errors.state)}
                                  helperText={touched.state && errors.state}
                                />
                              )}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <AppTextField
                              name='city'
                              value={values.city}
                              variant='outlined'
                              sx={{
                                width: '100%',
                              }}
                              label='City'
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <AppTextField
                              name='zip_code'
                              value={values.zip_code}
                              onChange={(event) => {
                                setFieldValue(
                                  'zip_code',
                                  event.target.value === ''
                                    ? null
                                    : event.target.value,
                                );
                              }}
                              variant='outlined'
                              sx={{
                                width: '100%',
                              }}
                              label='Zip Code'
                            />
                          </Grid>
                        </AppGridContainer>
                      </>
                    )}
                    {!isCoach(user) && (
                      <>
                        <Divider
                          sx={{
                            marginTop: 14,
                            marginBottom: 5,
                          }}
                        />
                        <Typography
                          component='h3'
                          sx={{
                            fontSize: 16,
                            fontWeight: Fonts.BOLD,
                            mb: { xs: 3, lg: 4 },
                            color: handleConditionFunction(
                              companyTheme,
                              companyTheme?.primary?.main,
                              'text.primary',
                            ),
                          }}
                        >
                          <IntlMessages id='common.parentInfo' />
                        </Typography>
                        <AppGridContainer sx={{ my: 2 }}>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                              name='parent1_name'
                              value={values.parent1_name}
                              onChange={(event) => {
                                setFieldValue(
                                  'parent1_name',
                                  event.target.value === ''
                                    ? null
                                    : event.target.value,
                                );
                              }}
                              variant='outlined'
                              sx={{
                                width: '100%',
                              }}
                              label='Parent1 Name'
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                              name='parent1_mobile'
                              value={values.parent1_mobile}
                              slotProps={{
                                input: { maxLength: 12 },
                              }}
                              error={!!errors.parent1_mobile}
                              helperText={errors.parent1_mobile}
                              onChange={(event) => {
                                setFieldValue(
                                  handleMobileFormat(event, 'parent1_mobile')
                                    .name,
                                  handleMobileFormat(event, 'parent1_mobile')
                                    .value,
                                );
                              }}
                              variant='outlined'
                              sx={{
                                width: '100%',
                              }}
                              label='Parent1 Mobile Number '
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <AppTextField
                              name='parent2_name'
                              value={values.parent2_name}
                              onChange={(event) => {
                                setFieldValue(
                                  'parent2_name',
                                  event.target.value === ''
                                    ? null
                                    : event.target.value,
                                );
                              }}
                              variant='outlined'
                              sx={{
                                width: '100%',
                              }}
                              label='Parent2 Name'
                            />
                          </Grid>

                          <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                              name='parent2_mobile'
                              value={values.parent2_mobile}
                              slotProps={{
                                input: { maxLength: 12 },
                              }}
                              error={!!errors.parent2_mobile}
                              helperText={errors.parent2_mobile}
                              onChange={(event) => {
                                setFieldValue(
                                  handleMobileFormat(event, 'parent2_mobile')
                                    .name,
                                  handleMobileFormat(event, 'parent2_mobile')
                                    .value,
                                );
                              }}
                              variant='outlined'
                              sx={{
                                width: '100%',
                              }}
                              label='Parent2 Mobile Number '
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <AppTextField
                              name='address'
                              value={values.address}
                              onChange={(event) => {
                                setFieldValue(
                                  'address',
                                  event.target.value === ''
                                    ? null
                                    : event.target.value,
                                );
                              }}
                              variant='outlined'
                              sx={{
                                width: '100%',
                              }}
                              label='Address'
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <AppTextField
                              name='zip_code'
                              value={values.zip_code}
                              onChange={(event) => {
                                setFieldValue(
                                  'zip_code',
                                  event.target.value === ''
                                    ? null
                                    : event.target.value,
                                );
                              }}
                              variant='outlined'
                              sx={{
                                width: '100%',
                              }}
                              label='Zip Code'
                            />
                          </Grid>
                        </AppGridContainer>
                      </>
                    )}
                    <Divider
                      sx={{
                        marginTop: 4,
                        marginBottom: 5,
                      }}
                    />
                    <Typography
                      component='h3'
                      sx={{
                        fontSize: 16,
                        fontWeight: Fonts.BOLD,
                        mb: { xs: 3, lg: 4 },
                        color: handleConditionFunction(
                          companyTheme,
                          companyTheme?.primary?.main,
                          'text.primary',
                        ),
                      }}
                    >
                      <IntlMessages id='common.emergencyInfo' />
                    </Typography>
                    <AppGridContainer sx={{ my: 2 }}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          name='emergency_contact_name'
                          value={values.emergency_contact_name}
                          onChange={(event) => {
                            setFieldValue(
                              'emergency_contact_name',
                              event.target.value === ''
                                ? null
                                : event.target.value,
                            );
                          }}
                          variant='outlined'
                          sx={{
                            width: '100%',
                          }}
                          label='Emergency Contact Name'
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          name='emergency_contact_number'
                          value={values.emergency_contact_number}
                          slotProps={{
                            input: { maxLength: 12 },
                          }}
                          onChange={(event) => {
                            setFieldValue(
                              handleMobileFormat(
                                event,
                                'emergency_contact_number',
                              ).name,
                              handleMobileFormat(
                                event,
                                'emergency_contact_number',
                              ).value,
                            );
                            touched.emergency_contact_number = true;
                          }}
                          error={!!errors.emergency_contact_number}
                          helperText={errors.emergency_contact_number}
                          variant='outlined'
                          sx={{
                            width: '100%',
                          }}
                          label='Emergency Contact Number'
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          name='relation'
                          value={values.relation}
                          onChange={(event) => {
                            setFieldValue(
                              'relation',
                              event.target.value === ''
                                ? null
                                : event.target.value,
                            );
                          }}
                          variant='outlined'
                          sx={{
                            width: '100%',
                          }}
                          label='Relation'
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 12 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {hasPermission(
                            rolePermissions,
                            'My Locker - Account Info',
                            'update',
                          ) && (
                            <Button
                              sx={{
                                position: 'relative',
                                minWidth: 100,
                                backgroundColor: handleConditionFunction(
                                  companyTheme,
                                  companyTheme?.primary?.main,
                                  '#2196f3',
                                ),
                                '&:hover': {
                                  backgroundColor: handleConditionFunction(
                                    companyTheme,
                                    alpha(companyTheme?.primary?.main, 0.8),
                                    alpha('#2196f3', 0.8),
                                  ),
                                },
                              }}
                              variant='contained'
                              type='submit'
                            >
                              Save
                            </Button>
                          )}
                          <Button
                            sx={{
                              position: 'relative',
                              minWidth: 100,
                              ml: 2.5,
                              borderColor: handleConditionFunction(
                                companyTheme,
                                companyTheme?.primary?.main,
                                '#2196f3',
                              ),
                              '&:hover': {
                                borderColor: handleConditionFunction(
                                  companyTheme,
                                  alpha(companyTheme?.primary?.main, 0.8),
                                  alpha('#2196f3', 0.8),
                                ),
                              },
                              color: handleConditionFunction(
                                companyTheme,
                                companyTheme?.primary?.main,
                                '#2196f3',
                              ),
                            }}
                            variant='outlined'
                            type='button'
                            onClick={(e) => {
                              e.preventDefault();
                              resetForm();
                            }}
                          >
                            <IntlMessages id='common.cancel' />
                          </Button>
                        </Box>
                      </Grid>
                    </AppGridContainer>
                  </Grid>
                </Form>
              </div>
            );
          }}
        </Formik>
      </Box>
      <AppInfoView />
    </>
  );
};

export default CommonAccountInfo;

CommonAccountInfo.propTypes = {
  fetchData: PropTypes.func,
  settings: PropTypes.object,
};
